import { useState } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { SectionHeaderWithControls } from '@components/SectionHeaderWithControls';
import { EmptyState } from '@components/EmptyState';
import { useResponsive } from '../../../shared/hooks/useResponsive';
import { usePartsDeletion } from '../../hooks/usePartsDeletion';
import type { WorkOrderPart, PartsListProps } from '../../types/parts.types';
import { PartCard } from './PartCard';
import { AddPartModal } from '../AddPart/AddPartModal';
import { DeletePartModal } from '../DeletePart/DeletePartModal';

type Props = PartsListProps & {
  parts: WorkOrderPart[];
  isLoading: boolean;
  hidePricing?: boolean;
};

export const PartsList = ({
  parts,
  isLoading,
  jobNumber,
  isReadOnly = false,
  hidePricing = false
}: Props) => {
  const { t } = useTranslation('parts');
  const { isMobile } = useResponsive();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const {
    isDeleteModalOpen,
    partToDelete,
    isDeleting,
    handleDeletePart,
    handleConfirmDelete,
    handleCloseModal
  } = usePartsDeletion(jobNumber);

  const handleAddPart = () => setIsAddModalOpen(true);

  return (
    <>
      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 200
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: 'auto'
          }}
        >
          <SectionHeaderWithControls
            title={t('title')}
            count={parts.length}
            canCreate={false}
            horizontalPadding="0px"
            pb={1}
            button={
              !isReadOnly && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddPart}
                  size={isMobile ? 'small' : 'medium'}
                >
                  {t('addPart')}
                </Button>
              )
            }
          >
            <></>
          </SectionHeaderWithControls>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: isMobile ? 1 : 2,
              overflowY: 'auto',
              overflowX: 'hidden',
              flexGrow: 1,
              px: isMobile ? 1 : 0
            }}
          >
            {parts.length > 0 ? (
              parts.map(part => {
                const canDelete =
                  !isReadOnly &&
                  part.sequenceNumber !== undefined &&
                  part.sequenceNumber > 0;
                return (
                  <PartCard
                    key={`${part.companyName}-${part.partNumber}`}
                    part={part}
                    onDelete={
                      canDelete ? () => handleDeletePart(part) : undefined
                    }
                    hidePricing={hidePricing}
                  />
                );
              })
            ) : (
              <Box sx={{ mt: 4 }}>
                <EmptyState message={t('noParts')} />
              </Box>
            )}
          </Box>
        </Box>
      )}

      <AddPartModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={() => setIsAddModalOpen(false)}
        jobNumber={jobNumber}
        existingParts={parts}
      />

      {isDeleteModalOpen && partToDelete && (
        <DeletePartModal
          open={isDeleteModalOpen}
          onClose={handleCloseModal}
          onConfirm={handleConfirmDelete}
          partName={
            partToDelete.partName || partToDelete.partNumber || 'this part'
          }
          isDeleting={isDeleting}
        />
      )}
    </>
  );
};
